import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear auth and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updatePassword: (data) => api.put('/auth/updatepassword', data),
  getUsers: () => api.get('/auth/users'),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
};

// Patient API calls
export const patientAPI = {
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  getTodayBirthdays: () => api.get('/patients/birthdays/today'),
};

// Prescription API calls
export const prescriptionAPI = {
  getByPatientId: (patientId) => api.get(`/prescriptions/patient/${patientId}`),
  getById: (id) => api.get(`/prescriptions/${id}`),
  create: (data) => api.post('/prescriptions', data),
  update: (id, data) => api.put(`/prescriptions/${id}`, data),
  delete: (id) => api.delete(`/prescriptions/${id}`),
};

// Medicine API calls
export const medicineAPI = {
  getAll: (params) => api.get('/medicines', { params }),
  getById: (id) => api.get(`/medicines/${id}`),
  create: (data) => api.post('/medicines', data),
  update: (id, data) => api.put(`/medicines/${id}`, data),
  delete: (id) => api.delete(`/medicines/${id}`),
};

// Visitation API calls
export const visitationAPI = {
  getByPatientId: (patientId) => api.get(`/visitations/patient/${patientId}`),
  getById: (id) => api.get(`/visitations/${id}`),
  create: (data) => api.post('/visitations', data),
  update: (id, data) => api.put(`/visitations/${id}`, data),
  delete: (id) => api.delete(`/visitations/${id}`),
};

// Treatment API calls
export const treatmentAPI = {
  getByPatientId: (patientId) => api.get(`/treatments/patient/${patientId}`),
  getById: (id) => api.get(`/treatments/${id}`),
  create: (data) => api.post('/treatments', data),
  update: (id, data) => api.put(`/treatments/${id}`, data),
  delete: (id) => api.delete(`/treatments/${id}`),
  getSuggestions: (patientId) => api.get(`/treatments/suggestions/${patientId}`),
  complete: (id, data) => api.post(`/treatments/${id}/complete`, data),
  getUpcomingReminders: () => api.get('/treatments/upcoming-reminders'),
};

// Dashboard API calls
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getCharts: () => api.get('/dashboard/charts'),
  getRecentActivities: (limit) => api.get('/dashboard/recent-activities', { params: { limit } }),
};

// Session API calls
export const sessionAPI = {
  getByTreatmentId: (treatmentId) => api.get(`/sessions/treatment/${treatmentId}`),
  getById: (id) => api.get(`/sessions/${id}`),
  create: (data) => api.post('/sessions', data),
  update: (id, data) => api.put(`/sessions/${id}`, data),
  delete: (id) => api.delete(`/sessions/${id}`),
};

// Billing API calls
export const billingAPI = {
  // Invoices
  getInvoices: (params) => api.get('/billing/invoices', { params }),
  getInvoiceById: (id) => api.get(`/billing/invoices/${id}`),
  createInvoice: (data) => api.post('/billing/invoices', data),
  updateInvoice: (id, data) => api.put(`/billing/invoices/${id}`, data),
  deleteInvoice: (id, data) => api.delete(`/billing/invoices/${id}`, { data, params: data }),
  addPayment: (invoiceId, data) => api.post(`/billing/invoices/${invoiceId}/payments`, data),
  // Payments
  getPayments: (params) => api.get('/billing/payments', { params }),
  // Reports
  getReport: (params) => api.get('/billing/reports', { params }),
};

// Sales API calls
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getById: (id) => api.get(`/sales/${id}`),
  getByBillNumber: (billNumber) => api.get(`/sales/bill/${billNumber}`),
  create: (data) => api.post('/sales', data),
  refund: (data) => api.post('/sales/refund', data),
  getRefunds: (params) => api.get('/sales/refunds', { params }),
  delete: (id) => api.delete(`/sales/${id}`),
};

export default api;
